/**
 * The EditorForm component
 */
import React, { useState } from "react";
import { Field, reduxForm, reset as resetReduxForm } from "redux-form";
import FieldInput from "../FieldInput";
import {
  Container,
  Row,
  Col,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Alert,
} from "reactstrap";
import PropTypes from "prop-types";
import AddToCartModal from "../CheckoutCart/utils/AddToCartModal";
import { useDispatch, useSelector } from "react-redux";
import EditorFormSection from "../EditorFormSection";
import { resetSize } from "../../redux/editor/size";
import { addToCart } from "../../redux/editor/shop";
import {
  addItemToCart,
  resetCartState as resetCart,
} from "../../redux/shop/cart";
import { VINYLS, VINYLTYPES } from "../../utils/vinyls";
import EditorFormHelpers from "./utils/BlankCutEditorFormHelpers";
import FormattedNumber from "../FormattedNumber";
import VinylSelectField from "../VinylSelectField";
import { FaTimes, FaChevronRight } from "react-icons/fa";
import { push } from "connected-react-router";
import DataLayerHelper from "../../utils/dataLayer";
import { resetCookieError } from "../../redux/shop/errors";
/**
 * Form to handle update and create items
 */
const EditorForm = (props, context) => {
  const { change, previewContent, handleSubmit } = props;
  const {
    size,
    language,
    addingToCart,
    addedToCart,
    addToShopCartSuccess,
    addToCartProcessing,
    addToCartError,
    cart,
  } = useSelector((state) => {
    return {
      size: state.editorSize,
      language: state.i18nState.lang,
      addingToCart: state.editorShop.loading,
      addedToCart: state.editorShop.loaded,
      addToShopCartSuccess: state.shopCart.addToShopCartSuccess,
      addToCartProcessing: state.shopCart.addToCartProcessing,
      addToCartError: state.shopCart.error,
      cookieError: state.shopErrors.cookieError,
      cart: state.shopCart.cart,
    };
  });

  const { color, materialSerie, height, width } = useSelector((state) => ({
    materialSerie: state.form.blankCutEditorForm.values.materialSerie,
    height: state.form.blankCutEditorForm.values.height,
    width: state.form.blankCutEditorForm.values.width,
    color: state.form.blankCutEditorForm.values.color,
  }));

  const [addToCartErrorModal, setAddToCartErrorModal] = useState(false);
  const [open, setOpen] = useState("");
  const [sizeChanged, setSizeChanged] = useState(false);
  const dispatch = useDispatch();

  const toggleAddToCartErrorModal = () => {
    if (addToCartError) {
      dispatch(resetCart());
      dispatch(resetCookieError());
    } else {
      setAddToCartErrorModal((prev) => !prev);
    }
  };

  const togglePanel = (panelName) => {
    setOpen((prevOpen) => (prevOpen !== panelName ? panelName : ""));
  };

  const handleMaterialTypeChange = (e, newMaterial, oldMaterial) => {
    const selectedVinyls = VINYLS.filter(
      (vinyl) => vinyl.series === VINYLTYPES[newMaterial].key
    );
    const firstColorForNewMaterial = selectedVinyls[0];
    change(`color`, {
      value: firstColorForNewMaterial,
      label: firstColorForNewMaterial.title,
    });
  };
  const handleHeightChange = (e) => {
    e.preventDefault();
    let newSize = parseInt(e.target.value.replace(/[^\d]+/g, ""));
    change("height", newSize);
    if (!sizeChanged) {
      setSizeChanged(true);
    }
  };

  const handleWidthChange = (e) => {
    e.preventDefault();
    let newSize = parseInt(e.target.value.replace(/[^\d]+/g, ""));
    change("width", newSize);
    if (!sizeChanged) {
      setSizeChanged(true);
    }
  };

  const onSubmit = (values) => {
    const max_length = color ? color.value.roll_length || 50000 : 50000;
    const roll_width = color ? color.value.roll_width || 1260 : 1260;

    const translations = {
      title: context.t("Individual Blank Vinyl Cut"),
      yes: context.t("Yes"),
      no: context.t("No"),
      width: context.t("Width"),
      height: context.t("Length"),
      material: context.t("Material"),
      series: context.t(`${color.value.series}`),
      color: context.t("Color"),
    };

    const json = EditorFormHelpers.generateJson(values, language, translations);

    if (isNaN(json.width) || isNaN(json.height) || isNaN(json.amount)) {
      // TODO handle here case when number inputs hold text. Just on IE
      // showAlert(alertOptions);
      DataLayerHelper.addEventWithOrigin(
        "error",
        "blank cut editor",
        "onSubmit",
        "values NaN"
      );
    } else if (
      !(width === roll_width || height === roll_width) &&
      (height > max_length || width > max_length)
    ) {
      toggleAddToCartErrorModal();
      DataLayerHelper.addEventWithOrigin(
        "error",
        "blank cut editor",
        "onSubmit",
        `dimensions too big: ${width} x ${height}`
      );
    } else if (
      (width > roll_width - 60 && width < roll_width) ||
      (height > roll_width - 60 && height < roll_width)
    ) {
      toggleAddToCartErrorModal();
      DataLayerHelper.addEventWithOrigin(
        "error",
        "blank cut editor",
        "onSubmit",
        `dimensions cutting boundaries: ${width} x ${height}`
      );
    } else if (width < 30 || height < 30) {
      toggleAddToCartErrorModal();
      DataLayerHelper.addEventWithOrigin(
        "error",
        "blank cut editor",
        "onSubmit",
        `dimensons too small: ${width} x ${height}`
      );
    } else {
      dispatch(
        addItemToCart({
          variant_id: 1809, // FIXME hardcoded test value should be product default variant id
          quantity: json.amount,
          options: {
            project_uid: json.sku,
            project_meta: JSON.stringify({
              title: json.productTitle,
              description: json.description,
              width: json.width,
              height: json.height,
              vinyl: json.vinyl,
              color: json.color,
              thumbnail: "https://cdn.myfolie.de/media/individualcut-nav.png",
              uid: json.sku,
              price: json.price,
              priceBeforeDiscountAndTax: json.priceBeforeDiscountAndTax,
            }),
          },
        })
      );
    }
  };

  const renderDimensionWarning = () => {
    const max_length = color ? color.value.roll_length || 50000 : 50000;
    const roll_width = color ? color.value.roll_width || 1260 : 1260;
    return (
      <div className="editor-from--warnings">
        {(width === roll_width || height === roll_width) && (
          <Alert color="success">
            {context.t(
              "Good: Your size matches our roll width, you are making optimal use of the vinyl."
            )}
          </Alert>
        )}

        {!(width === roll_width || height === roll_width) &&
          (height > max_length || width > max_length) && (
            <Alert color="danger">
              {context.t(
                "Sorry, we can't produce your vinyl in this size. Please try setting width or length to our default roll width to one of the following measueres - we can delivery those up to 50 meters:"
              )}
              <ul>
                <li>
                  <FormattedNumber
                    value={Math.round(roll_width * 100) / 100}
                    language={language}
                    unit="mm"
                    decimals={0}
                  />
                </li>
                {/* <li><FormattedNumber value={Math.round(roll_width / 2 * 100) / 100} language={language} unit="mm" decimals={0} /></li> */}
              </ul>
            </Alert>
          )}

        {((width > roll_width - 60 && width < roll_width) ||
          (height > roll_width - 60 && height < roll_width)) && (
          <Alert color="danger">
            {context.t(
              "Unfortunately we are technically limited to cut this dimensions. We need at least 30mm on each side for an exact cutting result if your size does not match our standard roll width."
            )}
          </Alert>
        )}

        {(width < 30 || height < 30) && (
          <Alert color="danger">
            {context.t(
              "Unfortunately we are technically limited to cut this dimensions. The minimum width and length is 30mm."
            )}
          </Alert>
        )}

        {((width > roll_width && height > roll_width) ||
          width > 50000 ||
          height > 50000) && (
          <Alert color="warning">
            {context.t(
              "Your dimensions exceed the limits to be produced in one piece. We are going to deliver your adhesive in multiple parts. The boundaries for one piece are:"
            )}{" "}
            {context.t("Length")}:{" "}
            <FormattedNumber
              value={Math.round(max_length * 100) / 100}
              language={language}
              unit="mm"
              decimals={0}
            />
            , {context.t("Width")}:{" "}
            <FormattedNumber
              value={Math.round(roll_width * 100) / 100}
              language={language}
              unit="mm"
              decimals={0}
            />
            .
          </Alert>
        )}
      </div>
    );
  };

  const renderFormErrors = () => {
    const { addToCartError, width, height, color, language, cookieError } =
      props;
    const max_length = color ? color.value.roll_length || 50000 : 50000;
    const roll_width = color ? color.value.roll_width || 1260 : 1260;

    if (
      !(width === roll_width || height === roll_width) &&
      (height > max_length || width > max_length)
    ) {
      return (
        <>
          {context.t(
            "Sorry, we can't produce your vinyl in this size. Please try setting width or length to our default roll width to one of the following measueres - we can delivery those up to 50 meters:"
          )}
          <ul>
            <li>
              <FormattedNumber
                value={Math.round(roll_width * 100) / 100}
                language={language}
                unit="mm"
                decimals={0}
              />
            </li>
            {/* <li><FormattedNumber value={Math.round(roll_width / 2 * 100) / 100} language={language} unit="mm" decimals={0} /></li> */}
          </ul>
        </>
      );
    }

    if (
      (width > roll_width - 60 && width < roll_width) ||
      (height > roll_width - 60 && height < roll_width)
    ) {
      return context.t(
        "Unfortunately we are technically limited to cut this dimensions. We need at least 30mm on each side for an exact cutting result if your size does not match our standard roll width."
      );
    }

    if (width < 30 || height < 30) {
      return context.t(
        "Unfortunately we are technically limited to cut this dimensions. The minimum width and length is 30mm."
      );
    }
    if (cookieError && addToCartError)
      return (
        context.t("An error occurred while adding your data to the cart. ") +
        context.t(
          "Cookies are disabled in your browser. Please enable mandatory cookies to add an item to your cart."
        )
      );
    if (!cookieError && addToCartError)
      return context.t(
        "An error occurred while adding your data to the cart. Please try again later"
      );

    return "";
  };

  const resetForm = (skipResetCart) => {
    dispatch(resetReduxForm("blankCutEditorForm"));
    dispatch(resetSize());

    if (!skipResetCart) {
      dispatch(resetCart());
      DataLayerHelper.addEventWithOrigin(
        "behaviour",
        "blank cut editor",
        "resetForm"
      );
    }
  };

  const calculatedPrice = EditorFormHelpers.calculatePrice({
    width,
    height,
    materialSerie,
    color,
    size,
  });
  const selectedMaterial = VINYLTYPES[materialSerie] || VINYLTYPES[0];

  return (
    <Container className="texteditor px-0" fluid>
      <Row className="texteditor__row no-gutters">
        <Col
          md={{ size: 4 }}
          className="order-md-2 texteditor__sidebar shadow-left"
        >
          <div className="p-3 blankcuteditor__sidebar__heading">
            <h1 className="h5 mb-0">
              {context.t("Your individual blank cut vinyl")}
            </h1>
          </div>
          <form>
            <EditorFormSection
              help={context.t("BlankCutEditor.dimensions.help")}
              panelName={"panel1"}
              panelTitle={context.t("Dimensions of your vinyl")}
              open
              keepOpen
            >
              <Row>
                <Col xs={6}>
                  <Field
                    disabled={addingToCart}
                    type="number"
                    className={
                      isNaN(width) || parseInt(width) < 10
                        ? "is-invalid data-hj-allow"
                        : "data-hj-allow"
                    }
                    label={context.t("Width")}
                    onChange={handleWidthChange}
                    name="width"
                    component={FieldInput}
                    addonType="append"
                    addonText="mm"
                    min={10}
                  />
                  <div className="flex-fill text-center pb-3 mt-n3">
                    {!isNaN(width) && !!width && (
                      <span className="text-remark">
                        <FormattedNumber
                          value={Math.round(width * 100) / 100}
                          language={language}
                          unit="mm"
                          decimals={1}
                        />
                        &nbsp;=&nbsp;
                        <FormattedNumber
                          value={Math.round(width * 10) / 100}
                          language={language}
                          unit="cm"
                          decimals={2}
                        />
                      </span>
                    )}
                  </div>
                </Col>
                <Col xs={6}>
                  <Field
                    disabled={addingToCart}
                    className={
                      isNaN(height) || parseInt(height) < 10
                        ? "is-invalid data-hj-allow"
                        : "data-hj-allow"
                    }
                    type="number"
                    label={context.t("Length")}
                    onChange={handleHeightChange}
                    name="height"
                    component={FieldInput}
                    addonType="append"
                    addonText="mm"
                    min={10}
                  />
                  <div className="flex-fill text-center pb-3 mt-n3">
                    {!isNaN(height) && !!height && (
                      <span className="text-remark">
                        <FormattedNumber
                          value={Math.round(height * 100) / 100}
                          language={language}
                          unit="mm"
                          decimals={1}
                        />
                        &nbsp;=&nbsp;
                        <FormattedNumber
                          value={Math.round(height * 10) / 100}
                          language={language}
                          unit="cm"
                          decimals={2}
                        />
                      </span>
                    )}
                  </div>
                </Col>
              </Row>
              {renderDimensionWarning()}
            </EditorFormSection>

            <EditorFormSection
              help={context.t("BlankCutEditor.materialType.help")}
              panelName={"panel2"}
              panelTitle={context.t("type of vinyl and color")}
              open
              keepOpen
            >
              <Field
                onChange={handleMaterialTypeChange}
                disabled={addingToCart}
                id={`materialSerie`}
                type="select"
                name={`materialSerie`}
                component={FieldInput}
              >
                {Object.keys(VINYLTYPES).map((prod) => (
                  <option value={prod} key={`product_${prod}`}>
                    {context.t(`${VINYLTYPES[prod].title}`)}
                  </option>
                ))}
              </Field>
              <div style={{ display: "none" }}>
                <span>{context.t("651 Intermediate Cal")}</span>
                <span>{context.t("631 Exhibition Cal")}</span>
                <span>{context.t("751C High Performance")}</span>
                <span>{context.t("951 Premium Cast")}</span>
                <span>{context.t("6510 Fluorescent Cast")}</span>
                <span>{context.t("8300 Transparent Cal")}</span>
                <span>{context.t("8510 Etched Glass Cal")}</span>
                <span>{context.t("8810 Frosted Glass Cal")}</span>
                <span>{context.t("Blackboard")}</span>
                <span>{context.t("WBL995")}</span>
              </div>
              <Field
                className="texteditor__sidebar__linesetting__color"
                name={`color`}
                component={VinylSelectField}
                materialSerie={materialSerie || 1}
              />
            </EditorFormSection>
            <EditorFormSection
              help={context.t("BlankCutEditor.summary.help")}
              panelName={"panel6"}
              panelTitle={context.t("summary")}
              open={open === "panel6"}
              toggle={togglePanel}
            >
              <div className="editorform-resume">
                <div>
                  <span>{context.t("Width")}:&nbsp;</span>
                  <FormattedNumber
                    value={Math.round(width * 100) / 100}
                    language={language}
                    unit="mm"
                    decimals={1}
                  />
                  &nbsp;=&nbsp;
                  <FormattedNumber
                    value={Math.round(width * 10) / 100}
                    language={language}
                    unit="cm"
                    decimals={2}
                  />
                </div>
                <div>
                  <span>{context.t("Length")}:&nbsp;</span>
                  <FormattedNumber
                    value={Math.round(height * 100) / 100}
                    language={language}
                    unit="mm"
                    decimals={1}
                  />
                  &nbsp;=&nbsp;
                  <FormattedNumber
                    value={Math.round(height * 10) / 100}
                    language={language}
                    unit="cm"
                    decimals={2}
                  />
                </div>
                {color && materialSerie && (
                  <div>
                    <span>{context.t("Color")}:&nbsp;</span>
                    {color.value.title} (
                    {context.t(`${selectedMaterial.title}`)})
                  </div>
                )}
              </div>
            </EditorFormSection>
            <div className="texteditor__sidebar__submit">
              <div className="d-flex align-items-center justify-content-between">
                <div className="texteditor__sidebar__submit__amount pl-3">
                  <Field
                    disabled={addingToCart}
                    type="number"
                    name="amount"
                    min={1}
                    component={FieldInput}
                  />
                </div>
                <div className="texteditor__sidebar__submit__times text-center">
                  <FaTimes className="fa-lg fa-fw text-white" />
                </div>
                <div className="texteditor__sidebar__submit__button">
                  <Button
                    color="primary"
                    block
                    type="button"
                    disabled={addingToCart}
                    onClick={handleSubmit(onSubmit)}
                  >
                    <div className="d-flex flex-column flex-lg-row justify-content-between">
                      <strong>
                        <FormattedNumber
                          value={calculatedPrice.finalPrice}
                          language={language}
                          unit="€"
                          decimals={2}
                        />
                      </strong>
                      <strong>
                        {context.t("Add to Cart")}
                        <FaChevronRight className="fa-fw" />
                      </strong>
                    </div>
                  </Button>
                  {(addToShopCartSuccess || addToCartProcessing) && (
                    <AddToCartModal
                      addItemToCart={addItemToCart}
                      productCount={cart?.data?.attributes?.item_count}
                      language={language}
                      // FIXME maybe not the best place to do, but okay for production
                      extraProducts={
                        cart?.included?.filter((item) => {
                          return (
                            item?.type === "line_item" &&
                            item?.attributes?.sku === "7004"
                          );
                        }).length > 0
                          ? ["print", "text"]
                          : ["print", "text", "squeegee"]
                      }
                      ctaTitle={context.t("customize another vinyl film")}
                      navigateTo={(url) => dispatch(push(url))}
                      addingToCart={addingToCart}
                      addedToCart={addedToCart}
                      addToCartError={addToCartError}
                      addToShopCartSuccess={addToShopCartSuccess}
                      addToCartProcessing={addToCartProcessing}
                      ctaAction={resetForm}
                      resetCart={() => dispatch(resetCart())}
                    />
                  )}

                  <Modal
                    isOpen={addToCartErrorModal || !!addToCartError}
                    toggle={toggleAddToCartErrorModal}
                    centered
                  >
                    <ModalHeader toggle={toggleAddToCartErrorModal}>
                      {context.t("Alert")}
                    </ModalHeader>
                    <ModalBody>
                      <div>
                        <div className="d-flex justify-content-center mb-3">
                          <FaTimes
                            className="fa-fw fa-5x text-danger"
                            aria-hidden="true"
                          />
                        </div>
                        <p>{renderFormErrors()}</p>
                      </div>
                    </ModalBody>
                    <ModalFooter>
                      <Button
                        color="primary"
                        onClick={toggleAddToCartErrorModal}
                      >
                        {context.t("Close")}
                      </Button>{" "}
                    </ModalFooter>
                  </Modal>
                </div>
              </div>
              <div className="small px-2 bg-light text-center">
                {context.t("price per item")}:{" "}
                <FormattedNumber
                  value={calculatedPrice.finalPrice}
                  language={language}
                  unit="€"
                  decimals={2}
                />
                &#160;-&#160;
                {context.t("incl. VAT")}
                &#160;-&#160;
                <a
                  href={`/${language}/p/versandarten/`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {context.t("plus shipping")}
                </a>
              </div>
            </div>
          </form>
        </Col>
        <Col md={{ size: 8 }} className="texteditor__preview p-3 order-md-1">
          {previewContent}
        </Col>
      </Row>
    </Container>
  );
};

/**  define proptype for the 'translation' function  */
EditorForm.contextTypes = {
  t: PropTypes.func,
};

/** define proptypes for the form */
EditorForm.propTypes = {
  /** function called on submit */
  handleSubmit: PropTypes.func.isRequired,
};

/** export the form as reduxForm component to bind it to the redux store */
// export default reduxForm({  form: 'blankCutEditorForm' })(EditorForm)

export default reduxForm({
  form: "blankCutEditorForm", // a unique identifier for this form
  enableReinitialize: true,
})(EditorForm);
